import DOMPurify from 'dompurify';
import { cleanPastedHTML } from '@/components/ui/RichTextEditor/helpers';
import { keepOnlyFontSizeStyles, normalizeRichTextValue } from '@/lib/rich-text';

/**
 * Tags a pasted text block may keep.
 *
 * Sized to what the DOCX export can actually render (see `htmlRenderer.ts` in
 * the api repo): anything outside this list would survive in the preview and
 * then silently flatten to plain text on download. Deliberately not the
 * `CLIPBOARD_ALLOWED_TAGS` list in `@/lib/rich-text` — that one serves the
 * opposite direction (copy-out serialization) and widening it would change how
 * copied rich text is flattened.
 */
const PASTE_ALLOWED_TAGS = [
    'p',
    'br',
    'div',
    'strong',
    'b',
    'em',
    'i',
    'u',
    's',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'blockquote',
    // Carries the font-size mark; keepOnlyFontSizeStyles below throws away
    // every other declaration, and unwraps the spans left holding nothing.
    'span',
];

/**
 * Turns clipboard `text/html` into the HTML a text block stores.
 *
 * `ALLOWED_ATTR: ['style']` drops Google Docs' ids and every href/class, then
 * `keepOnlyFontSizeStyles` narrows the one surviving attribute to a font-size,
 * which is what keeps Word's `mso-*` payload out. DOMPurify keeps the *content*
 * of tags it removes, so unwrapping `<a>` or a stray `<table>` loses no words.
 *
 * Returns `''` when nothing meaningful survives, which doubles as the
 * "is there anything to paste" test.
 */
export const clipboardHtmlToBlockHtml = (html: string): string => {
    if (!html) return '';

    const clean = keepOnlyFontSizeStyles(
        DOMPurify.sanitize(html, {
            ALLOWED_TAGS: PASTE_ALLOWED_TAGS,
            ALLOWED_ATTR: ['style'],
        }),
    );

    // Same normalizer Tiptap runs via `transformPastedHTML`, so pasting onto the
    // canvas and pasting into an open editor produce identical paragraphs.
    return normalizeRichTextValue(cleanPastedHTML(clean));
};

const escapeHtml = (value: string) =>
    value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Turns clipboard `text/plain` into paragraphs: a blank line starts a new
 * paragraph, a single newline is a soft break.
 */
export const plainTextToBlockHtml = (text: string): string => {
    if (!text) return '';

    return text
        .replace(/\r\n?/g, '\n')
        .split(/\n{2,}/)
        .filter((paragraph) => paragraph.trim() !== '')
        .map((paragraph) => `<p>${paragraph.split('\n').map(escapeHtml).join('<br />')}</p>`)
        .join('');
};
